import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./index.css";
import { FaTrash, FaPlus } from "react-icons/fa";
import Loader from "../../Components/Loader/index.js";
import GeneralToast from "../../Components/Toaster/index.js";
import { useCreateDigitalAssetDefinition } from "../../API/Queries/useCreateDigitalAssetDefinition.js";
import { useUpdateDigitalAsset } from "../../API/Queries/useupdateDigitalAsset.js";
import Select from "react-select";
import {
  InitiatorTypeOptions,
  OperationOptions,
  ModeOptions,
  InputTypeOptions,
} from "../../Utils/constants";

const CreateDigitalAssetDefinition = () => {
  const navigate = useNavigate();
  const { mutate: createRequest, isLoading } = useCreateDigitalAssetDefinition();
  const { mutate: updateRequest } = useUpdateDigitalAsset();
  
  const [expandedSections, setExpandedSections] = useState({});
  const [toast, setToast] = useState({ type: "", message: "" });
  
  // Refs for scrolling to newly added items
  const actionRefs = useRef({});
  const metadataRefs = useRef({});
  const referenceDocRefs = useRef({});

  const location = useLocation();
  const prefillData = location.state?.prefillData;
  const isEditMode = location.pathname === "/dam/edit-digital-asset-definition";

  const [formData, setFormData] = useState(() => {
    const data = prefillData || {};

    return {
      id: data.id || "",
      name: data.name || "",
      expiryDetail: data.expiryDetail || { label: "", days: 0 },
      actions: Array.isArray(data.actions)
        ? data.actions.map((action) => ({
                  name: action.name || "",
                  initiatorType: action.initiatorType || "",
                  operation: action.operation || "",
                  mode: action.mode || "",
            inputType: Array.isArray(action.inputType) ? action.inputType : [],
            provider: Array.isArray(action.provider) ? action.provider : [],
          }))
        : [],
      tags: data.tags || null,
      metadata: Array.isArray(data.metadata)
        ? data.metadata.map((meta) => ({
            type: meta.type || "",
            fieldType: meta.fieldType || "",
            label: meta.label || "",
            key: meta.key || "",
            prefillKey: meta.prefillKey || null,
            options: Array.isArray(meta.options) ? meta.options : null,
            optional: meta.optional || null,
          }))
        : [],
      rejectionDetails: data.rejectionDetails || {
        classification: [],
        subClassification: [],
      },
      referenceDocs: Array.isArray(data.referenceDocs)
        ? data.referenceDocs.map((doc) => ({
            mediaUrl: doc.mediaUrl || "",
            storageProvider: doc.storageProvider || "",
            fileCreationType: doc.fileCreationType || "",
          }))
        : [],
      deleted: data.deleted || false,
    };
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Function to scroll to newly added item
  const scrollToNewItem = (ref, index) => {
    setTimeout(() => {
      if (ref.current && ref.current[index]) {
        ref.current[index].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100); // Small delay to ensure DOM is updated
  };

  // Actions Management
  const addAction = () => {
    const newIndex = formData.actions.length;
    setFormData((prev) => ({
      ...prev,
      actions: [
        ...prev.actions,
        {
          name: "",
          initiatorType: "",
          operation: "",
          mode: "",
          inputType: [],
          provider: [],
        },
      ],
    }));
    // Expand actions section and scroll to new item
    setExpandedSections((prev) => ({ ...prev, actions: true }));
    scrollToNewItem(actionRefs, newIndex);
  };

  const updateAction = (index, field, value) => {
    const updatedActions = [...formData.actions];
    updatedActions[index][field] = value;
    setFormData({ ...formData, actions: updatedActions });
  };

  const removeAction = (index) => {
    setFormData((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  // Metadata Management
  const addMetadata = () => {
    const newIndex = formData.metadata.length;
    setFormData((prev) => ({
      ...prev,
      metadata: [
        ...prev.metadata,
        {
          type: "",
          fieldType: "",
          label: "",
          key: "",
          prefillKey: null,
          options: null,
          optional: null,
        },
      ],
    }));
    // Expand metadata section and scroll to new item
    setExpandedSections((prev) => ({ ...prev, metadata: true }));
    scrollToNewItem(metadataRefs, newIndex);
  };

  const updateMetadata = (index, field, value) => {
    const updatedMetadata = [...formData.metadata];
    updatedMetadata[index][field] = value;
    setFormData({ ...formData, metadata: updatedMetadata });
  };

  const removeMetadata = (index) => {
    setFormData((prev) => ({
      ...prev,
      metadata: prev.metadata.filter((_, i) => i !== index),
    }));
  };

  // Rejection Details Management
  const updateRejectionClassification = (value) => {
    setFormData((prev) => ({
      ...prev,
      rejectionDetails: {
        ...prev.rejectionDetails,
        classification: value.split('\n').filter(item => item.trim()),
      },
    }));
  };

  const updateRejectionSubClassification = (value) => {
    setFormData((prev) => ({
      ...prev,
      rejectionDetails: {
        ...prev.rejectionDetails,
        subClassification: value.split('\n').filter(item => item.trim()),
      },
    }));
  };

  // Reference Docs Management
  const addReferenceDoc = () => {
    const newIndex = formData.referenceDocs.length;
    setFormData((prev) => ({
      ...prev,
      referenceDocs: [
        ...prev.referenceDocs,
        {
          mediaUrl: "",
          storageProvider: "",
          fileCreationType: "",
        },
      ],
    }));
    // Expand reference docs section and scroll to new item
    setExpandedSections((prev) => ({ ...prev, referenceDocs: true }));
    scrollToNewItem(referenceDocRefs, newIndex);
  };

  const updateReferenceDoc = (index, field, value) => {
    const updatedDocs = [...formData.referenceDocs];
    updatedDocs[index][field] = value;
    setFormData({ ...formData, referenceDocs: updatedDocs });
  };

  const removeReferenceDoc = (index) => {
    setFormData((prev) => ({
      ...prev,
      referenceDocs: prev.referenceDocs.filter((_, i) => i !== index),
    }));
  };

  const cleanObject = (obj) => {
    if (Array.isArray(obj)) {
      return obj
        .map(cleanObject)
        .filter((item) => item !== null && item !== undefined);
    } else if (typeof obj === "object" && obj !== null) {
      const cleaned = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0) &&
          !(typeof value === "object" && Object.keys(value).length === 0)
        ) {
          cleaned[key] = cleanObject(value);
        }
      });
      return Object.keys(cleaned).length > 0 ? cleaned : null;
    }
    return obj;
  };

  const handleSave = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "This field is required";
    if (formData.actions.length === 0)
      newErrors.actions = "At least one action is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Transform data to match API requirements
    const transformedActions = formData.actions.map(action => ({
      name: action.name,
      initiatorType: action.initiatorType.trim().toUpperCase(), // Remove spaces and ensure uppercase
      operation: action.operation,
      mode: action.mode.toUpperCase(), // Ensure uppercase
      inputType: action.inputType.map(type => type.trim().toUpperCase()), // Remove spaces and ensure uppercase
      provider: action.provider,
    }));

    const transformedMetadata = formData.metadata.map(meta => ({
      type: meta.type.toLowerCase(), // Ensure lowercase
      fieldType: meta.fieldType,
      label: meta.label,
      key: meta.key,
      prefillKey: meta.prefillKey,
      options: meta.options,
      optional: meta.optional,
    }));

    const rawPayload = {
      name: formData.name,
      expiryDetail: formData.expiryDetail.label || formData.expiryDetail.days ? formData.expiryDetail : null,
      actions: transformedActions,
      tags: formData.tags,
      metadata: transformedMetadata,
      rejectionDetails: formData.rejectionDetails,
      referenceDocs: formData.referenceDocs,
      deleted: formData.deleted,
    };

    const cleanedPayload = cleanObject(rawPayload);

    const mutationFn = isEditMode ? updateRequest : createRequest;
    mutationFn(
      isEditMode
        ? { id: formData.id, payload: cleanedPayload }
        : cleanedPayload,
      {
        onSuccess: () => {
          setToast({
            type: "success",
            message: isEditMode
              ? "Digital Asset Definition updated successfully!"
              : "Digital Asset Definition created successfully!",
          });
          setTimeout(() => {
            navigate(-1);
          }, 1500);
        },
        onError: (err) => {
          console.error("Save failed", err);
          setToast({ type: "error", message: "Failed to save Digital Asset Definition." });
        },
      }
    );
  };

  const handleDiscard = () => {
    navigate(-1);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {toast.message && (
        <GeneralToast type={toast.type} message={toast.message} />
      )}
      <div className="crt-wrapper-container">
        <div className="crt-header-container">
          <div className="crt-header-box">
            <h3 className="text-base font-bold">
              {isEditMode
                ? "Edit Digital Asset Definition"
                : "Create Digital Asset Definition"}
            </h3>
          </div>
        </div>

        <div className="crt-content-container">
          <div className="crt-inner-wrapper">
            <div className="crt-input-group">
              {/* Basic Information */}
              <div className="crt-field-row">
                <label className="crt-label">
                  Name <span style={{ color: "red" }}>*</span>
                </label>
                <div className="crt-column">
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="crt-input-aligned"
                    placeholder="Enter Digital Asset Definition name"
                  />
                  {errors.name && (
                    <div className="crt-error-text">{errors.name}</div>
                  )}
                </div>
              </div>

              <div className="crt-field-row">
                <label className="crt-label">Expiry Detail</label>
                <div className="crt-column">
                  <div className="crt-field-row" style={{ marginBottom: "8px" }}>
                    <label className="crt-label" style={{ width: "120px" }}>Label:</label>
                    <input
                      name="expiryDetailLabel"
                      value={formData.expiryDetail?.label || ""}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          expiryDetail: {
                            ...prev.expiryDetail,
                            label: e.target.value
                          }
                        }));
                      }}
                      className="crt-input-aligned"
                      placeholder="Enter label (e.g., Upload Date)"
                    />
                  </div>
                  <div className="crt-field-row">
                    <label className="crt-label" style={{ width: "120px" }}>Days:</label>
                    <input
                      name="expiryDetailDays"
                      type="number"
                      value={formData.expiryDetail?.days || ""}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          expiryDetail: {
                            ...prev.expiryDetail,
                            days: parseInt(e.target.value) || 0
                          }
                        }));
                      }}
                      className="crt-input-aligned"
                      placeholder="Enter number of days"
                    />
                  </div>
                </div>
              </div>

              <div className="crt-field-row">
                <label className="crt-label">Tags</label>
                <input
                  name="tags"
                  value={formData.tags || ""}
                  onChange={handleChange}
                  className="crt-input-aligned"
                  placeholder="Enter tags (comma separated)"
                />
              </div>

              {/* Actions Section */}
              <div className="crt-section-header">
                <h4
                  onClick={() => toggleSection('actions')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {expandedSections.actions ? "▼" : "▶"} Actions
                  <span style={{ color: "red" }}>*</span>
                </h4>
                <button className="crt-add-btn" onClick={addAction}>
                  <FaPlus />
                </button>
              </div>

              {expandedSections.actions && (
                <div className="crt-section-content">
                  {formData.actions.length === 0 ? (
                    <div className="crt-no-data-message">
                      No actions available. Click the + button to add an action.
                    </div>
                  ) : (
                    formData.actions.map((action, index) => (
                    <div 
                      key={index} 
                      className="crt-action-box"
                      ref={(el) => (actionRefs.current[index] = el)}
                    >
                      <div className="crt-metadata-header">
                        <h5>Action {index + 1}</h5>
                        <button
                          className="crt-remove-btn"
                          onClick={() => removeAction(index)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                      
                      <div className="crt-field-row">
                        <label className="crt-label">Name</label>
                        <input
                          value={action.name}
                          onChange={(e) => updateAction(index, 'name', e.target.value)}
                          className="crt-input-aligned"
                          placeholder="Action name"
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Initiator Type</label>
                        <Select
                          options={InitiatorTypeOptions}
                          value={InitiatorTypeOptions.find(option => option.value === action.initiatorType)}
                          onChange={(selectedOption) => updateAction(index, 'initiatorType', selectedOption?.value || '')}
                          placeholder="Select Initiator Type"
                          className="crt-select-aligned"
                          isClearable
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Operation</label>
                        <Select
                          options={OperationOptions}
                          value={OperationOptions.find(option => option.value === action.operation)}
                          onChange={(selectedOption) => updateAction(index, 'operation', selectedOption?.value || '')}
                          placeholder="Select Operation"
                          className="crt-select-aligned"
                          isClearable
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Mode</label>
                        <Select
                          options={ModeOptions}
                          value={ModeOptions.find(option => option.value === action.mode)}
                          onChange={(selectedOption) => updateAction(index, 'mode', selectedOption?.value || '')}
                          placeholder="Select Mode"
                          className="crt-select-aligned"
                          isClearable
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Input Type</label>
                        <Select
                          options={InputTypeOptions}
                          value={Array.isArray(action.inputType) ? action.inputType.map(type => InputTypeOptions.find(option => option.value === type)).filter(Boolean) : []}
                          onChange={(selectedOptions) => updateAction(index, 'inputType', selectedOptions ? selectedOptions.map(option => option.value) : [])}
                          placeholder="Select Input Type(s)"
                          className="crt-select-aligned"
                          isMulti
                          isClearable
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Provider</label>
                        <input
                          value={Array.isArray(action.provider) ? action.provider.join(', ') : ''}
                          onChange={(e) => updateAction(index, 'provider', e.target.value.split(',').map(item => item.trim()).filter(item => item))}
                          className="crt-input-aligned"
                          placeholder="e.g., ninjacart (comma separated)"
                        />
                  </div>
                </div>
              ))
                  )}
                  {errors.actions && (
                    <div className="crt-error-text">{errors.actions}</div>
                  )}
                </div>
              )}

              {/* Metadata Section */}
              <div className="crt-section-header">
                <h4
                  onClick={() => toggleSection('metadata')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {expandedSections.metadata ? "▼" : "▶"} Metadata
                </h4>
                <button className="crt-add-btn" onClick={addMetadata}>
                  <FaPlus />
                </button>
              </div>

              {expandedSections.metadata && (
                <div className="crt-section-content">
                  {formData.metadata.length === 0 ? (
                    <div className="crt-no-data-message">
                      No metadata fields available. Click the + button to add a metadata field.
                    </div>
                  ) : (
                    formData.metadata.map((meta, index) => (
                    <div 
                      key={index} 
                      className="crt-metadata-box"
                      ref={(el) => (metadataRefs.current[index] = el)}
                    >
                      <div className="crt-metadata-header">
                        <h5>Metadata Field {index + 1}</h5>
                        <button
                          className="crt-remove-btn"
                          onClick={() => removeMetadata(index)}
                        >
                          <FaTrash />
                        </button>
                          </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Type</label>
                        <input
                          value={meta.type}
                          onChange={(e) => updateMetadata(index, 'type', e.target.value)}
                          className="crt-input-aligned"
                          placeholder="e.g., string, number"
                        />
                      </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Field Type</label>
                        <input
                          value={meta.fieldType}
                          onChange={(e) => updateMetadata(index, 'fieldType', e.target.value)}
                          className="crt-input-aligned"
                          placeholder="e.g., text, dropdown, date"
                        />
                                </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Label</label>
                        <input
                          value={meta.label}
                          onChange={(e) => updateMetadata(index, 'label', e.target.value)}
                          className="crt-input-aligned"
                          placeholder="Field label"
                        />
                                </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Key</label>
                        <input
                          value={meta.key}
                          onChange={(e) => updateMetadata(index, 'key', e.target.value)}
                          className="crt-input-aligned"
                          placeholder="Field key"
                        />
                                </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Prefill Key</label>
                        <input
                          value={meta.prefillKey || ''}
                          onChange={(e) => updateMetadata(index, 'prefillKey', e.target.value || null)}
                          className="crt-input-aligned"
                          placeholder="Prefill key (optional)"
                        />
                                </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Options</label>
                        <input
                          value={Array.isArray(meta.options) ? meta.options.join(', ') : ''}
                          onChange={(e) => updateMetadata(index, 'options', e.target.value.split(',').map(item => item.trim()).filter(item => item))}
                          className="crt-input-aligned"
                          placeholder="Options (comma separated, for dropdowns)"
                        />
                                </div>

                      <div className="crt-field-row">
                        <label className="crt-label">Optional</label>
                        <input
                          type="checkbox"
                          checked={meta.optional || false}
                          onChange={(e) => updateMetadata(index, 'optional', e.target.checked)}
                        />
                      </div>
                                  </div>
                                ))
                  )}
                            </div>
                          )}

              {/* Rejection Details Section */}
              <div className="crt-section-header">
                <h4
                  onClick={() => toggleSection('rejection')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {expandedSections.rejection ? "▼" : "▶"} Rejection Details
                          </h4>
              </div>

              {expandedSections.rejection && (
                <div className="crt-section-content">
                  <div className="crt-field-row">
                    <label className="crt-label">Classification</label>
                    <textarea
                      value={formData.rejectionDetails.classification.join('\n')}
                      onChange={(e) => updateRejectionClassification(e.target.value)}
                      className="crt-textarea"
                      placeholder="Enter classification options (one per line)"
                      rows="5"
                    />
                                          </div>

                  <div className="crt-field-row">
                    <label className="crt-label">Sub Classification</label>
                    <textarea
                      value={formData.rejectionDetails.subClassification.join('\n')}
                      onChange={(e) => updateRejectionSubClassification(e.target.value)}
                      className="crt-textarea"
                      placeholder="Enter sub-classification options (one per line)"
                      rows="3"
                    />
                                          </div>
                                          </div>
                                        )}

              {/* Reference Docs Section */}
              <div className="crt-section-header">
                <h4
                  onClick={() => toggleSection('referenceDocs')}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {expandedSections.referenceDocs ? "▼" : "▶"} Reference Documents
                </h4>
                <button className="crt-add-btn" onClick={addReferenceDoc}>
                  <FaPlus />
                </button>
              </div>

              {expandedSections.referenceDocs && (
                <div className="crt-section-content">
                  {formData.referenceDocs.length === 0 ? (
                    <div className="crt-no-data-message">
                      No reference documents available.Click the + button to add a reference documents field
                    </div>
                  ) : (
                    formData.referenceDocs.map((doc, index) => (
                      <div 
                        key={index} 
                        className="crt-reference-doc-box"
                        ref={(el) => (referenceDocRefs.current[index] = el)}
                      >
                        <div className="crt-reference-doc-header">
                          <h5>Reference Document {index + 1}</h5>
                          <button
                            className="crt-remove-btn"
                            onClick={() => removeReferenceDoc(index)}
                          >
                            <FaTrash />
                          </button>
                                          </div>
                        
                        <div className="crt-field-row">
                          <label className="crt-label">Media URL</label>
                          <input
                            value={doc.mediaUrl}
                            onChange={(e) => updateReferenceDoc(index, 'mediaUrl', e.target.value)}
                            className="crt-input-aligned"
                            placeholder="Media URL"
                          />
                        </div>

                        <div className="crt-field-row">
                          <label className="crt-label">Storage Provider</label>
                          <input
                            value={doc.storageProvider}
                            onChange={(e) => updateReferenceDoc(index, 'storageProvider', e.target.value)}
                            className="crt-input-aligned"
                            placeholder="e.g., S3"
                          />
                        </div>

                        <div className="crt-field-row">
                          <label className="crt-label">File Creation Type</label>
                          <input
                            value={doc.fileCreationType}
                            onChange={(e) => updateReferenceDoc(index, 'fileCreationType', e.target.value)}
                            className="crt-input-aligned"
                            placeholder="e.g., USER_UPLOAD"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Deleted Field */}
              <div className="crt-field-row">
                <label className="crt-label">Deleted</label>
                <label className="crt-toggle small">
                  <input
                    type="checkbox"
                    name="deleted"
                    checked={formData.deleted}
                    onChange={handleChange}
                  />
                  <span className="crt-slider round"></span>
                </label>
              </div>

              <div className="crt-button-container">
                <button className="crt-save-btn" onClick={handleSave}>
                  {isEditMode ? "Update" : "Save Changes"}
                </button>

                <button className="crt-discard-btn" onClick={handleDiscard}>
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateDigitalAssetDefinition;